import { Injectable } from "@angular/core";
import { AbstractAppRouterService } from "../../application/services/abstract-app-router.service";
import { AuthenticationRouteId } from "../enums/authentication-route-id";
import {
    RedirectCommand, UrlTree
} from "@angular/router";
import { IAuthenticationAppRouterService } from "../interfaces/authentication-app-router-service";
import { ISidenavLink } from "../interfaces/sidenav-link";
import { AuthenticationToken } from "../models/authentication-token";
import { NavigationSource } from "../../application/enums/navigation-source";

/**
 * The main AppRouterService for an application should extend from this class if Authentication is enabled
 *
 * The service implements logic needed to interact with the Authentication App Routes
 *
 * This service is used by the AuthenticationService and its components.
 *
 * It can be injected in the CommonNg library using the AUTHENTICATION_APP_ROUTER_SERVICE token
 *
 * In an application it will be injected as the AppRouterService
 *
 * @see AUTHENTICATION_ROUTES
 * @see AuthenticationRouteId
 */
@Injectable()
export abstract class AbstractAuthenticationAppRouterService<R extends string> extends AbstractAppRouterService<R | AuthenticationRouteId>
    implements IAuthenticationAppRouterService {

    get sidenavLinks(): Array<Readonly<ISidenavLink>> {
        return this._mgdRouter.getSidenavLinks(this.featureGroupId) as Array<Readonly<ISidenavLink>>;
    }

    private _postLoginPath?: string;

    /**
     * Returns the temporary token value which was resolved from the "token" queryParam by the TemporaryTokenResolver
     *
     */
    get temporaryToken(): AuthenticationToken {

        return this._mgdRouter.activeRoute?.route?.snapshot?.data?.temporaryToken;
    }

    /**
     * Returns true if the login page is currently loaded
     */
    get isLogin(): boolean {
        return this.isRouteActive(AuthenticationRouteId.Login);
    }

    /**
     * Returns true if the logout page is currently loaded
     */
    get isLogout(): boolean {
        return this.isRouteActive(AuthenticationRouteId.Logout);
    }

    /**
     * Returns true if the accept invitation page is currently loaded
     */
    get isAcceptInvitation(): boolean {
        return this.isRouteActive(AuthenticationRouteId.AcceptInvitation);
    }

    /**
     * Returns true if the forgot password page is currently loaded
     */
    get isForgotPassword(): boolean {
        return this.isRouteActive(AuthenticationRouteId.ForgotPassword);
    }

    /**
     * Returns true if the forgot password page is currently loaded
     */
    get isForgotPasswordRequest(): boolean {
        return this.isRouteActive(AuthenticationRouteId.ForgotPasswordRequest);
    }

    constructor() {
        super();
    }

    /**
     * navigate to the login route
     *
     * If navigationSource is not provided it will default to RouterService
     *
     * To force the navigation on a token expiry the navigationSource should be set to Error
     */
    toLogin(navigationSource?: NavigationSource): void {
        this._navigateByRouteId(AuthenticationRouteId.Login, undefined, undefined, navigationSource);
    }

    toAcceptInvitation(): void {
        this._navigateByRouteId(AuthenticationRouteId.AcceptInvitation);
    }

    toForgotPassword(): void {
        this._navigateByRouteId(AuthenticationRouteId.ForgotPassword);
    }

    toForgotPasswordRequest(): void {
        this._navigateByRouteId(AuthenticationRouteId.ForgotPasswordRequest);
    }

    toLogout(navigationSource?: NavigationSource): void {
        this._navigateByRouteId(AuthenticationRouteId.Logout, undefined, undefined, navigationSource);
    }

    getLoginUrl(): UrlTree {

        return this.getUrlTree(AuthenticationRouteId.Login);
    }

    setPostLoginPathFromLogout(): void {

        const nav = this._mgdRouter.currentNavigation;

        if (nav?.extras?.info === NavigationSource.Error) {
            // The error handler navigates to the logout endpoint when there's a token error
            // It will have a NavigationSource of Error
            // The logout forwarder calls this function before redirecting to the login page
            this._postLoginPath = nav.initialUrl.toString();
        }
    }

    setPostLoginPath(path: string): void {

        if (path === this.getPath(AuthenticationRouteId.Login)) {
            throw new Error("Cannot set postLogin path to the actual login path");
        }
        this._postLoginPath = path;
    }

    toPostLogin(): void {

        const url = this._postLoginPath ?? this._mgdRouter.rootPath;
        this._postLoginPath = undefined;
        this._mgdRouter.navigateByUrl(url, NavigationSource.RouterService, { relativeToCurrentRoute: true });
    }

    getLoginRedirectCommand(source?: NavigationSource): RedirectCommand {

        return this._mgdRouter.getRedirectCommand(this.getLoginUrl(), source ?? NavigationSource.RouterService);
    }
}
